import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Col, DatePicker, Input, Popconfirm, Row, Select, Space, Switch, Tag, TimePicker, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/tr';
import type { DayRange } from './types';
import type { PublicHoliday } from '../../../modules/public-holidays/types/PublicHoliday';
import { getPlanningInspectorContext } from '../../../modules/inspection-appointments/services/inspection-appointmentService';
import { buildBlockingPublicHolidayDateSet, buildVisiblePublicHolidayMap } from '../publicHolidayUtils';
import { openNotification, TRANSPORTATION_TYPE_OPTIONS } from '../../../utils';

dayjs.extend(customParseFormat);
dayjs.locale('tr');

const { Text } = Typography;

type PlanParameterRow = {
  key: string;
  attributeId?: number;
  quantity: number;
};

type DraftParameterSelection = {
  attributeId: number;
  name: string;
  quantity: number;
};

type ParameterOption = {
  attributeId: number;
  name: string;
  quantity?: number;
  offeringKey?: string;
  offeringLabel?: string;
};

type CreatePlanPayload = {
  inspectorId: number;
  dateKey: string;
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
  transportationType?: string;
  note?: string;
  parameters: Array<{ attributeId: number; quantity: number }>;
};

type PlannerInspector = {
  id: number;
  name: string;
  profession: string;
  attributeIds: number[];
  matchedProposalAttributesCount?: number;
  proposalTotalAttributeCount?: number;
};

type AssignmentEntry = {
  inspectorId: number;
  offeringLabel: string;
  start: dayjs.Dayjs;
  end: dayjs.Dayjs | null;
  source: 'draft' | 'current';
  appointmentId?: number;
  proposalId?: number;
  proposalNumber?: string;
  companyName?: string;
  city?: string;
  district?: string;
  transportation?: string;
  note?: string;
  offeringKey?: string;
  slotIndex?: number;
  parameters?: DraftParameterSelection[];
};

type LeaveEntry = {
  inspectorId: number;
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
  label: string;
};

type ReservationEntry = {
  inspectorId: number;
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
  label: string;
};

interface InspectorPlanningCardProps {
  proposalId?: number;
  loading: boolean;
  isReadOnly: boolean;
  inspectors: any[];
  onInspectorsLoaded?: (inspectors: any[]) => void;
  offeringDates: Record<string, DayRange[]>;
  offeringInspectors: Record<string, (number | undefined)[]>;
  offeringAppointmentIds: Record<string, (number | undefined)[]>;
  offeringTransports: Record<string, (string | undefined)[]>;
  offeringTransportNotes: Record<string, (string | undefined)[]>;
  offeringLabels: Record<string, string>;
  publicHolidays: PublicHoliday[];
  parameterOptions: ParameterOption[];
  plannedQuantityByAttributeId: Map<number, number>;
  draftParametersBySlot: Record<string, DraftParameterSelection[]>;
  onCreatePlan: (payload: CreatePlanPayload) => void;
  onUpdatePagePlan: (payload: {
    offeringKey: string;
    index: number;
    inspectorId: number;
    start: dayjs.Dayjs;
    end: dayjs.Dayjs;
    transportationType?: string;
    note?: string;
    parameters?: Array<{ attributeId: number; quantity: number }>;
  }) => void | boolean | Promise<boolean>;
  onDeletePagePlan: (payload: { offeringKey: string; index: number }) => void | Promise<void>;
}

const dayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

const startOfWeekMonday = (value: dayjs.Dayjs) => {
  const normalizedDay = (value.day() + 6) % 7;
  return value.startOf('day').subtract(normalizedDay, 'day');
};

const getWeekNavigationMonth = (value: dayjs.Dayjs) => value.add(6, 'day').startOf('month');

const formatMonthLabel = (value: dayjs.Dayjs) => {
  const monthLabel = value.locale('tr').format('MMMM YYYY');
  return monthLabel.charAt(0).toLocaleUpperCase('tr-TR') + monthLabel.slice(1);
};

const isDateOnlyMoment = (value: dayjs.Dayjs) =>
  value.hour() === 0 && value.minute() === 0 && value.second() === 0 && value.millisecond() === 0;

const buildBusyInterval = (
  startInput?: dayjs.Dayjs | null,
  endInput?: dayjs.Dayjs | null,
  options?: { allDay?: boolean },
): { start: dayjs.Dayjs; end: dayjs.Dayjs } | null => {
  if (!startInput || !dayjs.isDayjs(startInput) || !startInput.isValid()) {
    return null;
  }
  const start = startInput;
  const endCandidate =
    endInput && dayjs.isDayjs(endInput) && endInput.isValid()
      ? endInput
      : null;

  if (options?.allDay) {
    const dayStart = start.startOf('day');
    const dayEndBase = endCandidate && endCandidate.isValid() ? endCandidate : start;
    return {
      start: dayStart,
      end: dayEndBase.endOf('day'),
    };
  }

  if (endCandidate) {
    if (isDateOnlyMoment(start) && isDateOnlyMoment(endCandidate)) {
      return {
        start: start.startOf('day'),
        end: endCandidate.endOf('day'),
      };
    }
    if (!endCandidate.isAfter(start)) {
      return {
        start,
        end: start.add(1, 'minute'),
      };
    }
    return {
      start,
      end: endCandidate,
    };
  }

  return {
    start,
    end: start.add(1, 'minute'),
  };
};

const isTimeRangeOverlapping = (
  startA: dayjs.Dayjs,
  endA: dayjs.Dayjs,
  startB: dayjs.Dayjs,
  endB: dayjs.Dayjs,
) => startA.isBefore(endB) && endA.isAfter(startB);

const compareByStartTime = <T extends { start: dayjs.Dayjs }>(a: T, b: T) =>
  a.start.valueOf() - b.start.valueOf();

const normalizeText = (value: unknown): string | undefined => {
  if (value == null) return undefined;
  const text = String(value).trim();
  return text.length ? text : undefined;
};

const pickAddressValue = (address: any, keys: string[]): string | undefined => {
  if (!address || typeof address !== 'object') {
    return undefined;
  }
  for (const key of keys) {
    const value = normalizeText((address as any)?.[key]);
    if (value) {
      return value;
    }
  }
  return undefined;
};

const resolveCompanyName = (appointment: any): string | undefined =>
  normalizeText(
    appointment?.companyName ??
    appointment?.company?.name ??
    appointment?.proposal?.companyName ??
    appointment?.proposal?.company?.name,
  );

const resolveProposalNumber = (appointment: any): string | undefined =>
  normalizeText(
    appointment?.proposalNumber ??
    appointment?.proposal?.proposalNumber ??
    appointment?.proposal?.number,
  );

const resolveAppointmentLocation = (appointment: any): { city?: string; district?: string } => {
  const addressSources = [
    appointment?.address,
    appointment?.company?.address,
    appointment?.proposal?.address,
    appointment?.proposal?.company?.address,
  ];

  const cityFromRoot = normalizeText(
    appointment?.city ?? appointment?.cityName ?? appointment?.proposal?.city ?? appointment?.proposal?.cityName,
  );
  const districtFromRoot = normalizeText(
    appointment?.district ??
    appointment?.districtName ??
    appointment?.county ??
    appointment?.countyName ??
    appointment?.proposal?.district ??
    appointment?.proposal?.districtName,
  );

  const cityFromAddress = addressSources
    .map((address) => pickAddressValue(address, ['city', 'cityName', 'province', 'provinceName', 'state', 'il']))
    .find(Boolean);
  const districtFromAddress = addressSources
    .map((address) =>
      pickAddressValue(address, ['district', 'districtName', 'district_text', 'county', 'countyName', 'ilce', 'town']))
    .find(Boolean);

  return {
    city: cityFromRoot ?? cityFromAddress,
    district: districtFromRoot ?? districtFromAddress,
  };
};

const isDeletedOrCanceledAppointment = (appointment: any): boolean => {
  if (!appointment || typeof appointment !== 'object') {
    return false;
  }

  const status = normalizeText(appointment?.status)?.toUpperCase();
  if (status === 'CANCELED' || status === 'CANCELLED' || status === 'DELETED') {
    return true;
  }

  if (appointment?.isDeleted === true || appointment?.deleted === true) {
    return true;
  }

  if (appointment?.deletedAt != null || appointment?.deleted_at != null) {
    return true;
  }

  return false;
};

const InspectorPlanningCard: React.FC<InspectorPlanningCardProps> = ({
  proposalId,
  loading,
  isReadOnly,
  inspectors,
  onInspectorsLoaded,
  offeringDates,
  offeringInspectors,
  offeringAppointmentIds,
  offeringTransports,
  offeringTransportNotes,
  offeringLabels,
  publicHolidays,
  parameterOptions,
  plannedQuantityByAttributeId,
  draftParametersBySlot,
  onCreatePlan,
  onUpdatePagePlan,
  onDeletePagePlan,
}) => {
  const [weekStart, setWeekStart] = useState(() => startOfWeekMonday(dayjs()));
  const [focusedMonth, setFocusedMonth] = useState(() => dayjs().startOf('month'));
  const [searchText, setSearchText] = useState('');
  const [showAllInspectors, setShowAllInspectors] = useState(false);
  const [showSelectedInspectorsOnly, setShowSelectedInspectorsOnly] = useState(false);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [leaves, setLeaves] = useState<any[]>([]);
  const [reservations, setReservations] = useState<any[]>([]);
  const [contextInspectors, setContextInspectors] = useState<any[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [activePlanCell, setActivePlanCell] = useState<{ inspectorId: number; dateKey: string } | null>(null);
  const [planStartTime, setPlanStartTime] = useState(() => dayjs('09:00', 'HH:mm'));
  const [planEndTime, setPlanEndTime] = useState(() => dayjs('18:00', 'HH:mm'));
  const [planTransportType, setPlanTransportType] = useState<string>('DRIVER');
  const [planNote, setPlanNote] = useState('');
  const [planDate, setPlanDate] = useState<dayjs.Dayjs | null>(null);
  const [updatingPlan, setUpdatingPlan] = useState(false);
  const updatingPlanInFlight = useRef(false);
  const [planParameters, setPlanParameters] = useState<PlanParameterRow[]>([{ key: 'row-1', quantity: 1 }]);
  const [editingPagePlanTarget, setEditingPagePlanTarget] = useState<{ offeringKey: string; index: number } | null>(null);
  const transportLabelByValue = useMemo(() => {
    const map = new Map<string, string>();
    (TRANSPORTATION_TYPE_OPTIONS || []).forEach((option: any) => {
      if (!option || option.value == null) {
        return;
      }
      map.set(String(option.value), String(option.label ?? option.value));
    });
    return map;
  }, []);
  const parameterNameByAttributeId = useMemo(() => {
    const map = new Map<number, string>();
    (parameterOptions || []).forEach((option) => {
      const attributeId = Number(option?.attributeId);
      if (Number.isNaN(attributeId) || attributeId <= 0) {
        return;
      }
      map.set(attributeId, (option?.name || `Parametre #${attributeId}`).toString());
    });
    return map;
  }, [parameterOptions]);
  const parameterLimitByAttributeId = useMemo(() => {
    const map = new Map<number, number>();
    (parameterOptions || []).forEach((option) => {
      const attributeId = Number(option?.attributeId);
      const quantity = Number(option?.quantity);
      if (!Number.isNaN(attributeId) && attributeId > 0 && Number.isFinite(quantity) && quantity > 0) {
        map.set(attributeId, Math.floor(quantity));
      }
    });
    return map;
  }, [parameterOptions]);
  const editingSlotKey = editingPagePlanTarget
    ? `${editingPagePlanTarget.offeringKey}::${editingPagePlanTarget.index}`
    : undefined;
  const editingSlotQuantityByAttributeId = useMemo(() => {
    const map = new Map<number, number>();
    if (!editingSlotKey) {
      return map;
    }
    (draftParametersBySlot?.[editingSlotKey] || []).forEach((parameter) => {
      const attributeId = Number(parameter?.attributeId);
      if (Number.isNaN(attributeId) || attributeId <= 0) {
        return;
      }
      const quantity = Number(parameter?.quantity) || 0;
      if (quantity <= 0) {
        return;
      }
      map.set(attributeId, (map.get(attributeId) || 0) + quantity);
    });
    return map;
  }, [editingSlotKey, draftParametersBySlot]);
  const getAvailableQuantityForAttribute = useCallback(
    (attributeId?: number) => {
      const normalizedAttributeId = Number(attributeId);
      if (Number.isNaN(normalizedAttributeId) || normalizedAttributeId <= 0) {
        return undefined;
      }
      const totalLimit = parameterLimitByAttributeId.get(normalizedAttributeId);
      if (totalLimit == null) {
        return undefined;
      }
      const plannedQuantity = Number(plannedQuantityByAttributeId?.get(normalizedAttributeId)) || 0;
      const editableQuantity = Number(editingSlotQuantityByAttributeId.get(normalizedAttributeId)) || 0;
      return Math.max(0, totalLimit - Math.max(0, plannedQuantity - editableQuantity));
    },
    [parameterLimitByAttributeId, plannedQuantityByAttributeId, editingSlotQuantityByAttributeId],
  );
  const parameterSelectOptions = useMemo(
    () =>
      (parameterOptions || [])
        .map((option) => {
          const attributeId = Number(option?.attributeId);
          if (Number.isNaN(attributeId) || attributeId <= 0) {
            return null;
          }
          const totalLimit = parameterLimitByAttributeId.get(attributeId);
          const available = getAvailableQuantityForAttribute(attributeId);
          let label = (option?.name || `Parametre #${attributeId}`).toString();
          if (totalLimit != null) {
            label = `${label} (Kalan: ${available ?? 0}/${totalLimit})`;
          }
          return {
            label,
            value: attributeId,
          };
        })
        .filter((option): option is { label: string; value: number } => Boolean(option)),
    [parameterOptions, parameterLimitByAttributeId, getAvailableQuantityForAttribute],
  );

  const weekDays = useMemo(
    () => Array.from({ length: 7 }).map((_, idx) => weekStart.add(idx, 'day')),
    [weekStart],
  );
  const rangeLabel = weekDays.length
    ? `${weekDays[0].format('DD MMM YYYY')} - ${weekDays[6].format('DD MMM YYYY')}`
    : '';
  const focusedMonthLabel = useMemo(() => formatMonthLabel(focusedMonth), [focusedMonth]);
  const availableOfferingKeys = useMemo(
    () => Object.keys(offeringLabels || {}).filter((key) => key && key.trim().length > 0),
    [offeringLabels],
  );

  const holidayDateSet = useMemo(() => {
    return buildBlockingPublicHolidayDateSet(publicHolidays);
  }, [publicHolidays]);
  const visibleHolidayMap = useMemo(() => {
    return buildVisiblePublicHolidayMap(publicHolidays);
  }, [publicHolidays]);

  const inspectorSource = useMemo(() => {
    const merged = new Map<number, any>();
    const append = (value: any) => {
      const id = Number(value?.account?.id ?? value?.id ?? value?.accountId);
      if (!Number.isFinite(id)) {
        return;
      }
      if (!merged.has(id)) {
        merged.set(id, value);
      }
    };
    (Array.isArray(contextInspectors) ? contextInspectors : []).forEach(append);
    (Array.isArray(inspectors) ? inspectors : []).forEach(append);
    return Array.from(merged.values());
  }, [contextInspectors, inspectors]);

  const normalizedInspectors = useMemo<PlannerInspector[]>(() => {
    const list: PlannerInspector[] = [];
    inspectorSource.forEach((inspector) => {
      const id = Number(inspector?.account?.id ?? inspector?.id ?? inspector?.accountId);
      if (!Number.isFinite(id)) {
        return;
      }
      const firstName = (inspector?.account?.firstName ?? inspector?.firstName ?? '').toString().trim();
      const lastName = (inspector?.account?.lastName ?? inspector?.lastName ?? '').toString().trim();
      const name = `${firstName} ${lastName}`.trim() || `#${id}`;
      const professionNames = Array.isArray(inspector?.professions)
        ? inspector.professions
          .map((profession: any) => {
            if (!profession) {
              return '';
            }
            if (typeof profession === 'string') {
              return profession.trim();
            }
            return String(profession?.name ?? '').trim();
          })
          .filter((value: string) => value.length > 0)
        : [];
      const profession =
        professionNames.length > 0
          ? professionNames.join(', ')
          : inspector?.professionName || inspector?.title || 'Uzman';
      const parsedAttributeIds: number[] = (Array.isArray(inspector?.attributes) ? inspector.attributes : [])
        .map((attribute: any): number => Number(attribute?.id ?? attribute?.attributeId ?? attribute))
        .filter((attributeId: number) => Number.isFinite(attributeId) && attributeId > 0);
      const attributeIds = Array.from(new Set<number>(parsedAttributeIds));
      const matchedRaw = Number(
        inspector?.matchedProposalAttributesCount ??
        inspector?.matched_count ??
        inspector?.matchedProposalCount,
      );
      const totalRaw = Number(
        inspector?.proposalTotalAttributeCount ??
        inspector?.proposal_total_attribute_count ??
        inspector?.proposalTotalCount,
      );
      list.push({
        id,
        name,
        profession,
        attributeIds,
        matchedProposalAttributesCount: Number.isFinite(matchedRaw) ? matchedRaw : undefined,
        proposalTotalAttributeCount: Number.isFinite(totalRaw) ? totalRaw : undefined,
      });
    });
    return list;
  }, [inspectorSource]);

  useEffect(() => {
    if (!proposalId) {
      setAppointments([]);
      setLeaves([]);
      setReservations([]);
      setContextInspectors([]);
      onInspectorsLoaded?.([]);
      return;
    }

    let ignore = false;
    const load = async () => {
      const weekStartBoundary = weekStart.startOf('day');
      const weekEndBoundary = weekStart.add(6, 'day').startOf('day');
      try {
        setDataLoading(true);
        const response = await getPlanningInspectorContext(
          proposalId,
          weekStartBoundary.format('YYYY-MM-DD'),
          weekEndBoundary.format('YYYY-MM-DD'),
        );
        if (ignore) return;
        setAppointments(Array.isArray(response?.appointments) ? response.appointments : []);
        setLeaves(Array.isArray(response?.leaves) ? response.leaves : []);
        setReservations(Array.isArray(response?.reservations) ? response.reservations : []);
        const nextInspectors = Array.isArray(response?.inspectors) ? response.inspectors : [];
        setContextInspectors(nextInspectors);
        onInspectorsLoaded?.(nextInspectors);
      } catch (error) {
        if (!ignore) {
          setAppointments([]);
          setLeaves([]);
          setReservations([]);
          setContextInspectors([]);
          onInspectorsLoaded?.([]);
        }
        console.error('Inspector planning data fetch failed:', error);
      } finally {
        if (!ignore) {
          setDataLoading(false);
        }
      }
    };
    load();
    return () => {
      ignore = true;
    };
  }, [proposalId, weekStart, onInspectorsLoaded]);

  const inspectorIdSet = useMemo(
    () => new Set(normalizedInspectors.map((inspector) => inspector.id)),
    [normalizedInspectors],
  );
  const offeringMatchStatsByInspector = useMemo(() => {
    const attributeNameById = new Map<number, string>();
    (parameterOptions || []).forEach((option) => {
      const attributeId = Number(option?.attributeId);
      if (!Number.isFinite(attributeId) || attributeId <= 0) {
        return;
      }
      attributeNameById.set(attributeId, (option?.name || `Parametre #${attributeId}`).toString());
    });
    const proposalAttributeIdsByOffering = new Map<string, { label: string; ids: Set<number> }>();
    (parameterOptions || []).forEach((option) => {
      const attributeId = Number(option?.attributeId);
      if (!Number.isFinite(attributeId) || attributeId <= 0) {
        return;
      }
      const offeringKey = (option?.offeringKey || 'no::Diğer').toString();
      const offeringLabel = (option?.offeringLabel || offeringKey.split('::')[1] || 'Diğer').toString();
      const current = proposalAttributeIdsByOffering.get(offeringKey);
      if (current) {
        current.ids.add(attributeId);
        return;
      }
      proposalAttributeIdsByOffering.set(offeringKey, {
        label: offeringLabel,
        ids: new Set([attributeId]),
      });
    });

    const map = new Map<number, Array<{ offeringKey: string; label: string; matched: number; total: number; missingNames: string[] }>>();
    normalizedInspectors.forEach((inspector) => {
      const inspectorAttributeIdSet = new Set(inspector.attributeIds || []);
      const stats = Array.from(proposalAttributeIdsByOffering.entries())
        .map(([offeringKey, entry]) => {
          const total = entry.ids.size;
          const missingIds = Array.from(entry.ids).filter((attributeId) => !inspectorAttributeIdSet.has(attributeId));
          const matched = total - missingIds.length;
          const missingNames = missingIds
            .map((attributeId) => attributeNameById.get(attributeId) || `Parametre #${attributeId}`)
            .sort((a, b) => a.localeCompare(b, 'tr'));
          return {
            offeringKey,
            label: entry.label,
            matched,
            total,
            missingNames,
          };
        })
        .filter((entry) => entry.total > 0)
        .sort((a, b) => a.label.localeCompare(b.label, 'tr'));
      map.set(inspector.id, stats);
    });
    return map;
  }, [parameterOptions, normalizedInspectors]);

  const assignmentsByInspectorAndDay = useMemo(() => {
    const map = new Map<string, AssignmentEntry[]>();
    Object.keys(offeringDates || {}).forEach((offeringKey) => {
      const ranges = offeringDates?.[offeringKey] || [];
      const assignedInspectors = offeringInspectors?.[offeringKey] || [];
      const transports = offeringTransports?.[offeringKey] || [];
      const notes = offeringTransportNotes?.[offeringKey] || [];
      const offeringLabel = offeringLabels?.[offeringKey] || offeringKey;

      ranges.forEach((range, idx) => {
        const inspectorId = Number(assignedInspectors[idx]);
        const start = range?.[0];
        const end = range?.[1];
        const slotKey = `${offeringKey}::${idx}`;
        const parameters = (draftParametersBySlot?.[slotKey] || [])
          .map((parameter) => ({
            attributeId: Number(parameter?.attributeId),
            name: (parameter?.name || `Parametre #${parameter?.attributeId ?? ''}`).toString(),
            quantity: Number(parameter?.quantity) || 0,
          }))
          .filter((parameter) => !Number.isNaN(parameter.attributeId) && parameter.attributeId > 0 && parameter.quantity > 0);
        if (!Number.isFinite(inspectorId) || !start || !dayjs.isDayjs(start) || !start.isValid()) return;
        const dayKey = start.format('YYYY-MM-DD');
        const mapKey = `${inspectorId}-${dayKey}`;
        const arr = map.get(mapKey) || [];
        arr.push({
          inspectorId,
          offeringLabel,
          start,
          end: end && dayjs.isDayjs(end) && end.isValid() ? end : null,
          source: 'draft',
          appointmentId: offeringAppointmentIds[offeringKey]?.[idx],
          transportation: typeof transports[idx] === 'string' ? transports[idx] : undefined,
          note: typeof notes[idx] === 'string' ? notes[idx] : undefined,
          offeringKey,
          slotIndex: idx,
          parameters,
        });
        map.set(mapKey, arr);
      });
    });
    return map;
  }, [offeringDates, offeringInspectors, offeringAppointmentIds, offeringLabels, offeringTransports, offeringTransportNotes, draftParametersBySlot]);

  const appointmentAssignmentsByInspectorAndDay = useMemo(() => {
    const map = new Map<string, AssignmentEntry[]>();
    const pushEntry = (
      inspectorId: number,
      start: dayjs.Dayjs,
      end: dayjs.Dayjs | null,
      label: string,
      details?: {
        appointmentId?: number;
        proposalId?: number;
        proposalNumber?: string;
        companyName?: string;
        city?: string;
        district?: string;
      },
    ) => {
      const dayKey = start.format('YYYY-MM-DD');
      const key = `${inspectorId}-${dayKey}`;
      const list = map.get(key) || [];
      list.push({
        inspectorId,
        offeringLabel: label,
        start,
        end,
        source: 'current',
        appointmentId: details?.appointmentId,
        proposalId: details?.proposalId,
        proposalNumber: details?.proposalNumber,
        companyName: details?.companyName,
        city: details?.city,
        district: details?.district,
      });
      map.set(key, list);
    };

    (appointments || []).forEach((appointment) => {
      if (isDeletedOrCanceledAppointment(appointment)) {
        return;
      }

      const inspectorIdRaw =
        appointment?.account?.id ??
        appointment?.accountId ??
        appointment?.inspectorId ??
        appointment?.inspector?.id;
      const inspectorId = Number(inspectorIdRaw);
      if (!Number.isFinite(inspectorId) || !inspectorIdSet.has(inspectorId)) {
        return;
      }

      const startRaw = appointment?.startDate ?? appointment?.planningAt ?? appointment?.start_date;
      const startMoment = dayjs(startRaw);
      if (!startMoment.isValid()) {
        return;
      }
      const endRaw = appointment?.endDate ?? appointment?.planningEndDate ?? appointment?.end_date;
      const endMoment = endRaw ? dayjs(endRaw) : null;
      const validEnd = endMoment && endMoment.isValid() ? endMoment : null;

      const proposalNumber = resolveProposalNumber(appointment);
      const appointmentIdValue = Number(
        appointment?.id ??
        appointment?.appointmentId ??
        appointment?.appointment_id,
      );
      const proposalIdValueRaw =
        appointment?.proposalId ??
        appointment?.proposal?.id;
      const proposalIdValue = Number(proposalIdValueRaw);
      const companyName = resolveCompanyName(appointment);
      const { city, district } = resolveAppointmentLocation(appointment);
      const label = proposalNumber ?? companyName ?? 'Plan';

      pushEntry(inspectorId, startMoment, validEnd, String(label), {
        appointmentId: Number.isFinite(appointmentIdValue) && appointmentIdValue > 0 ? appointmentIdValue : undefined,
        proposalId: Number.isFinite(proposalIdValue) ? proposalIdValue : undefined,
        proposalNumber,
        companyName,
        city,
        district,
      });
    });

    return map;
  }, [appointments, inspectorIdSet]);

  const leaveByInspectorAndDay = useMemo(() => {
    const map = new Map<string, LeaveEntry>();
    const labelMap: Record<string, string> = {
      ANNUAL: 'Yıllık İzin',
      REPORT: 'Raporlu',
      UNPAID: 'Ücretsiz İzin',
      SICK: 'Sağlık İzni',
      ADMINISTRATIVE: 'İdari İzin',
      LEAVE: 'İzinli',
    };

    (leaves || []).forEach((leave) => {
      const inspectorIdRaw = leave?.account?.id ?? leave?.accountId;
      const inspectorId = Number(inspectorIdRaw);
      if (!Number.isFinite(inspectorId) || !inspectorIdSet.has(inspectorId)) {
        return;
      }

      const startMoment = dayjs(leave?.startDate);
      const endMomentCandidate = leave?.endDate ? dayjs(leave.endDate) : startMoment;
      if (!startMoment.isValid() || !endMomentCandidate.isValid()) {
        return;
      }
      const endMoment = endMomentCandidate.endOf('day');

      let cursor = startMoment.startOf('day');
      while (cursor.isBefore(endMoment) || cursor.isSame(endMoment, 'day')) {
        const key = `${inspectorId}-${cursor.format('YYYY-MM-DD')}`;
        const typeKey = typeof leave?.type === 'string' ? leave.type.toUpperCase() : 'LEAVE';
        map.set(key, {
          inspectorId,
          start: startMoment,
          end: endMomentCandidate,
          label: labelMap[typeKey] || 'İzinli',
        });
        cursor = cursor.add(1, 'day');
      }
    });

    return map;
  }, [leaves, inspectorIdSet]);

  const reservationsByInspectorAndDay = useMemo(() => {
    const map = new Map<string, ReservationEntry[]>();
    (reservations || []).forEach((reservation) => {
      const inspectorIdRaw =
        reservation?.account?.id ??
        reservation?.accountId ??
        reservation?.inspectorId ??
        reservation?.inspector?.id;
      const inspectorId = Number(inspectorIdRaw);
      if (!Number.isFinite(inspectorId) || !inspectorIdSet.has(inspectorId)) {
        return;
      }

      const startRaw =
        reservation?.reservationDate ??
        reservation?.date ??
        reservation?.startDate ??
        reservation?.reservationStartDate;
      const startMoment = dayjs(startRaw);
      const endRaw = reservation?.endDate ?? reservation?.reservationEndDate;
      const endMomentCandidate = endRaw ? dayjs(endRaw) : startMoment;
      if (!startMoment.isValid() || !endMomentCandidate.isValid()) {
        return;
      }
      const endMoment = endMomentCandidate.endOf('day');
      const label = (reservation?.note || '').toString().trim() || 'Rezervasyon';

      let cursor = startMoment.startOf('day');
      while (cursor.isBefore(endMoment) || cursor.isSame(endMoment, 'day')) {
        const key = `${inspectorId}-${cursor.format('YYYY-MM-DD')}`;
        const list = map.get(key) || [];
        list.push({
          inspectorId,
          start: startMoment,
          end: endMomentCandidate,
          label,
        });
        map.set(key, list);
        cursor = cursor.add(1, 'day');
      }
    });
    map.forEach((entries, key) => {
      map.set(key, [...entries].sort(compareByStartTime));
    });
    return map;
  }, [reservations, inspectorIdSet]);

  const inspectorLocationSearchById = useMemo(() => {
    const tokenMap = new Map<number, Set<string>>();
    appointmentAssignmentsByInspectorAndDay.forEach((entries) => {
      (entries || []).forEach((entry) => {
        const inspectorId = Number(entry?.inspectorId);
        if (Number.isNaN(inspectorId) || inspectorId <= 0) {
          return;
        }
        const tokens = tokenMap.get(inspectorId) || new Set<string>();
        if (entry?.city) {
          tokens.add(String(entry.city).toLocaleLowerCase('tr-TR'));
        }
        if (entry?.district) {
          tokens.add(String(entry.district).toLocaleLowerCase('tr-TR'));
        }
        tokenMap.set(inspectorId, tokens);
      });
    });
    const map = new Map<number, string>();
    tokenMap.forEach((tokens, inspectorId) => {
      map.set(inspectorId, Array.from(tokens).join(' '));
    });
    return map;
  }, [appointmentAssignmentsByInspectorAndDay]);

  const filteredInspectors = useMemo(() => {
    const term = searchText.trim().toLocaleLowerCase('tr-TR');
    const visibleInspectors = showAllInspectors
      ? normalizedInspectors
      : normalizedInspectors.filter((inspector) => {
        const matchedCount = Number(inspector.matchedProposalAttributesCount);
        return !Number.isFinite(matchedCount) || matchedCount > 0;
      });
    const selectedInspectorIdSet = new Set<number>();
    Object.values(offeringInspectors || {}).forEach((list) => {
      (list || []).forEach((value) => {
        const inspectorId = Number(value);
        if (!Number.isNaN(inspectorId) && inspectorId > 0) {
          selectedInspectorIdSet.add(inspectorId);
        }
      });
    });
    const inspectorsBySelection = showSelectedInspectorsOnly
      ? visibleInspectors.filter((inspector) => selectedInspectorIdSet.has(inspector.id))
      : visibleInspectors;
    if (!term) return inspectorsBySelection;
    return inspectorsBySelection.filter(
      (inspector) => {
        const locationSearch = inspectorLocationSearchById.get(inspector.id) || '';
        return (
          inspector.name.toLocaleLowerCase('tr-TR').includes(term) ||
          inspector.profession.toLocaleLowerCase('tr-TR').includes(term) ||
          locationSearch.includes(term)
        );
      },
    );
  }, [normalizedInspectors, offeringInspectors, searchText, showAllInspectors, showSelectedInspectorsOnly, inspectorLocationSearchById]);

  const openEditPlanForm = (entry: AssignmentEntry) => {
    if (isReadOnly || entry.source !== 'draft') {
      return;
    }
    if (!entry.offeringKey || entry.slotIndex == null) {
      return;
    }
    const dateKey = entry.start.format('YYYY-MM-DD');
    setActivePlanCell({ inspectorId: entry.inspectorId, dateKey });
    setPlanDate(entry.start.startOf('day'));
    setPlanStartTime(dayjs(entry.start.format('HH:mm'), 'HH:mm'));
    const resolvedEnd = entry.end && entry.end.isValid() ? entry.end : entry.start.add(1, 'hour');
    setPlanEndTime(dayjs(resolvedEnd.format('HH:mm'), 'HH:mm'));
    setPlanTransportType(entry.transportation || 'DRIVER');
    setPlanNote(entry.note || '');
    const seed = Date.now();
    const mappedParameters = (entry.parameters || [])
      .map((parameter, index) => ({
        key: `row-${seed}-${index}`,
        attributeId: Number(parameter?.attributeId),
        quantity: Math.max(1, Number(parameter?.quantity) || 1),
      }))
      .filter((parameter) => !Number.isNaN(Number(parameter.attributeId)) && Number(parameter.attributeId) > 0);
    setPlanParameters(mappedParameters.length > 0 ? mappedParameters : [{ key: `row-${seed}`, quantity: 1 }]);
    setEditingPagePlanTarget({ offeringKey: entry.offeringKey, index: entry.slotIndex });
  };

  const handleDeletePagePlan = (entry: AssignmentEntry) => {
    if (isReadOnly || entry.source !== 'draft') {
      return;
    }
    if (!entry.offeringKey || entry.slotIndex == null) {
      return;
    }
    return onDeletePagePlan({ offeringKey: entry.offeringKey, index: entry.slotIndex });
  };

  const openCreatePlanForm = (inspectorId: number, dateKey: string) => {
    if (isReadOnly) {
      return;
    }
    const targetDate = dayjs(dateKey, 'YYYY-MM-DD');
    if (!targetDate.isValid() || targetDate.isBefore(dayjs().startOf('day'), 'day')) {
      return;
    }
    if (activePlanCell?.inspectorId === inspectorId && activePlanCell?.dateKey === dateKey) {
      return;
    }
    setActivePlanCell({ inspectorId, dateKey });
    setPlanDate(targetDate);
    setPlanStartTime(dayjs('09:00', 'HH:mm'));
    setPlanEndTime(dayjs('18:00', 'HH:mm'));
    setPlanTransportType('DRIVER');
    setPlanNote('');
    setPlanParameters([{ key: `row-${Date.now()}`, quantity: 1 }]);
    setEditingPagePlanTarget(null);
  };

  const closeCreatePlanForm = () => {
    setActivePlanCell(null);
    setEditingPagePlanTarget(null);
  };

  const addPlanParameterRow = () => {
    setPlanParameters((prev) => [...prev, { key: `row-${Date.now()}-${prev.length}`, quantity: 1 }]);
  };

  const removePlanParameterRow = (key: string) => {
    setPlanParameters((prev) => (prev.length > 1 ? prev.filter((item) => item.key !== key) : prev));
  };

  const getMaxQuantityForRow = useCallback(
    (rows: PlanParameterRow[], rowKey: string, attributeId?: number) => {
      const normalizedAttributeId = Number(attributeId);
      if (Number.isNaN(normalizedAttributeId) || normalizedAttributeId <= 0) {
        return undefined;
      }
      const limit = getAvailableQuantityForAttribute(normalizedAttributeId);
      if (limit == null) {
        return undefined;
      }
      const usedByOtherRows = rows.reduce((sum, row) => {
        const rowAttributeId = Number(row?.attributeId);
        if (row.key === rowKey || rowAttributeId !== normalizedAttributeId) {
          return sum;
        }
        return sum + (Number(row?.quantity) || 0);
      }, 0);
      return Math.max(0, limit - usedByOtherRows);
    },
    [getAvailableQuantityForAttribute],
  );

  const handleParameterAttributeChange = (rowKey: string, nextAttributeId?: number) => {
    let blockedByMax = false;
    setPlanParameters((prev) =>
      prev.map((item) => {
        if (item.key !== rowKey) return item;
        const normalizedAttributeId = Number(nextAttributeId);
        if (Number.isNaN(normalizedAttributeId) || normalizedAttributeId <= 0) {
          return { ...item, attributeId: undefined };
        }
        const maxForRow = getMaxQuantityForRow(prev, rowKey, normalizedAttributeId);
        if (maxForRow != null && maxForRow <= 0) {
          blockedByMax = true;
          return item;
        }
        const nextQuantity = maxForRow != null ? Math.min(Number(item.quantity) || 1, maxForRow) : Number(item.quantity) || 1;
        return {
          ...item,
          attributeId: normalizedAttributeId,
          quantity: nextQuantity > 0 ? nextQuantity : 1,
        };
      }),
    );
    if (blockedByMax) {
      openNotification('warning', 'Uyarı', 'Bu parametre için maksimum adet sınırına ulaşıldı.');
    }
  };

  const handleParameterQuantityChange = (rowKey: string, nextQuantity?: number | null) => {
    setPlanParameters((prev) =>
      prev.map((item) => {
        if (item.key !== rowKey) return item;
        const normalizedQuantity = typeof nextQuantity === 'number' && nextQuantity > 0 ? Math.floor(nextQuantity) : 1;
        const maxForRow = getMaxQuantityForRow(prev, rowKey, item.attributeId);
        if (maxForRow != null) {
          return { ...item, quantity: Math.min(normalizedQuantity, Math.max(1, maxForRow)) };
        }
        return { ...item, quantity: normalizedQuantity };
      }),
    );
  };

  const handleCreatePlanSave = async () => {
    if (!activePlanCell || isReadOnly || updatingPlanInFlight.current) {
      return;
    }
    const isEditMode = Boolean(editingPagePlanTarget);
    const activeInspectorId = activePlanCell.inspectorId;
    const activeDayKey = isEditMode ? planDate?.format('YYYY-MM-DD') : activePlanCell.dateKey;
    if (!activeDayKey) {
      openNotification('error', 'Hata', 'Geçerli bir tarih seçiniz.');
      return;
    }
    const start = dayjs(`${activeDayKey} ${planStartTime.format('HH:mm')}`, 'YYYY-MM-DD HH:mm');
    const end = dayjs(`${activeDayKey} ${planEndTime.format('HH:mm')}`, 'YYYY-MM-DD HH:mm');
    if (!start.isValid() || !end.isValid()) {
      openNotification('error', 'Hata', 'Tarih veya saat bilgisi geçersiz.');
      return;
    }
    if (!end.isAfter(start)) {
      openNotification('error', 'Hata', 'Bitiş saati başlangıç saatinden sonra olmalıdır.');
      return;
    }

    if (holidayDateSet.has(activeDayKey)) {
      openNotification('error', 'Hata', 'Resmi tatil günlerinde plan oluşturulamaz.');
      return;
    }

    const mapKey = `${activeInspectorId}-${activeDayKey}`;
    const editTarget = editingPagePlanTarget;
    const editedAppointmentIdRaw =
      isEditMode && editTarget
        ? offeringAppointmentIds?.[editTarget.offeringKey]?.[editTarget.index]
        : undefined;
    const editedAppointmentId = Number(editedAppointmentIdRaw);
    const hasEditedAppointmentId = !Number.isNaN(editedAppointmentId) && editedAppointmentId > 0;
    const hasDraftOverlap = (assignmentsByInspectorAndDay.get(mapKey) || []).some((entry) => {
      if (isEditMode && hasEditedAppointmentId && entry.appointmentId === editedAppointmentId) return false;
      if (
        isEditMode &&
        editTarget &&
        entry.source === 'draft' &&
        entry.offeringKey === editTarget.offeringKey &&
        entry.slotIndex === editTarget.index
      ) {
        return false;
      }
      const busyInterval = buildBusyInterval(entry.start, entry.end);
      return busyInterval
        ? isTimeRangeOverlapping(start, end, busyInterval.start, busyInterval.end)
        : false;
    });
    if (hasDraftOverlap) {
      openNotification('error', 'Hata', 'Seçilen saat aralığı mevcut plan ile kesişiyor.');
      return;
    }

    const hasAppointmentOverlap = (appointmentAssignmentsByInspectorAndDay.get(mapKey) || []).some((entry) => {
      if (isEditMode && hasEditedAppointmentId && entry.appointmentId === editedAppointmentId) {
        return false;
      }
      const busyInterval = buildBusyInterval(entry.start, entry.end);
      return busyInterval
        ? isTimeRangeOverlapping(start, end, busyInterval.start, busyInterval.end)
        : false;
    });
    if (hasAppointmentOverlap) {
      openNotification('error', 'Hata', 'Seçilen saat aralığı mevcut randevu ile kesişiyor.');
      return;
    }

    const leaveEntry = leaveByInspectorAndDay.get(mapKey);
    if (leaveEntry) {
      const leaveInterval = buildBusyInterval(leaveEntry.start, leaveEntry.end, { allDay: true });
      if (
        leaveInterval &&
        isTimeRangeOverlapping(start, end, leaveInterval.start, leaveInterval.end)
      ) {
        openNotification('error', 'Hata', 'Seçilen saat aralığı izin kaydı ile kesişiyor.');
        return;
      }
    }

    const reservationEntries = reservationsByInspectorAndDay.get(mapKey) || [];
    const hasReservationOverlap = reservationEntries.some((reservationEntry) => {
      const reservationInterval = buildBusyInterval(reservationEntry.start, reservationEntry.end);
      return reservationInterval
        ? isTimeRangeOverlapping(start, end, reservationInterval.start, reservationInterval.end)
        : false;
    });
    if (hasReservationOverlap) {
      openNotification('error', 'Hata', 'Seçilen saat aralığı rezervasyon ile kesişiyor.');
      return;
    }

    if (isEditMode) {
      if (!editTarget) {
        return;
      }

      updatingPlanInFlight.current = true;
      setUpdatingPlan(true);
      try {
        const updated = await onUpdatePagePlan({
          offeringKey: editTarget.offeringKey,
          index: editTarget.index,
          inspectorId: activePlanCell.inspectorId,
          start,
          end,
          transportationType: planTransportType,
          note: planNote,
        });
        if (updated === false) return;
        setWeekStart(startOfWeekMonday(start));
        setFocusedMonth(start.startOf('month'));
        closeCreatePlanForm();
      } catch (error) {
        openNotification('error', 'Hata', error instanceof Error ? error.message : 'Plan satırı güncellenemedi.');
      } finally {
        updatingPlanInFlight.current = false;
        setUpdatingPlan(false);
      }
      return;
    }

    const autoParameters = (parameterOptions || [])
      .map((option) => {
        const attributeId = Number(option?.attributeId);
        if (Number.isNaN(attributeId) || attributeId <= 0) {
          return null;
        }
        const remaining = Number(getAvailableQuantityForAttribute(attributeId)) || 0;
        if (remaining <= 0) {
          return null;
        }
        return {
          attributeId,
          quantity: remaining,
        };
      })
      .filter((row): row is { attributeId: number; quantity: number } => Boolean(row));

    onCreatePlan({
      inspectorId: activePlanCell.inspectorId,
      dateKey: activePlanCell.dateKey,
      start,
      end,
      transportationType: planTransportType,
      note: planNote,
      parameters: autoParameters,
    });
    closeCreatePlanForm();
  };

  const renderCreatePlanForm = () => {
    if (!activePlanCell) return null;
    const isEditMode = Boolean(editingPagePlanTarget);
    const colSpan = weekDays.length + 1;
    const formattedDate = dayjs(activePlanCell.dateKey, 'YYYY-MM-DD').isValid()
      ? dayjs(activePlanCell.dateKey, 'YYYY-MM-DD').format('DD/MM/YYYY')
      : activePlanCell.dateKey;
    return (
      <tr key={`plan-form-${activePlanCell.inspectorId}-${activePlanCell.dateKey}`}>
        <td colSpan={colSpan} style={{ padding: 0, borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
          <div style={{ padding: '18px 20px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 20 }}>
                {isEditMode ? 'Plan Düzenle' : 'Yeni Plan Ekle'}
              </div>
              <Button onClick={closeCreatePlanForm} disabled={updatingPlan}>İptal</Button>
            </div>
            <Row gutter={16}>
              <Col span={6}>
                <Text strong>Tarih</Text>
                {isEditMode ? (
                  <DatePicker
                    aria-label='Randevu Tarihi'
                    value={planDate}
                    onChange={setPlanDate}
                    format='DD/MM/YYYY'
                    disabled={isReadOnly || updatingPlan}
                    disabledDate={(date) => holidayDateSet.has(date.format('YYYY-MM-DD'))}
                    style={{ marginTop: 8, width: '100%' }}
                  />
                ) : <Input value={formattedDate} disabled style={{ marginTop: 8 }} />}
              </Col>
              <Col span={6}>
                <Text strong>Başlangıç Saati</Text>
                <TimePicker
                  style={{ width: '100%', marginTop: 8 }}
                  format='HH:mm'
                  minuteStep={10}
                  value={planStartTime}
                  disabled={isReadOnly || updatingPlan}
                  onChange={(value) => setPlanStartTime(value || dayjs('09:00', 'HH:mm'))}
                />
              </Col>
              <Col span={6}>
                <Text strong>Bitiş Saati</Text>
                <TimePicker
                  style={{ width: '100%', marginTop: 8 }}
                  format='HH:mm'
                  minuteStep={10}
                  value={planEndTime}
                  disabled={isReadOnly || updatingPlan}
                  onChange={(value) => setPlanEndTime(value || dayjs('18:00', 'HH:mm'))}
                />
              </Col>
              <Col span={6}>
                <Text strong>Ulaşım Türü</Text>
                <Select
                  style={{ width: '100%', marginTop: 8 }}
                  value={planTransportType}
                  onChange={setPlanTransportType}
                  options={TRANSPORTATION_TYPE_OPTIONS}
                  disabled={isReadOnly || updatingPlan}
                />
              </Col>
            </Row>

            <div style={{ marginTop: 14 }}>
              <Text strong>Not</Text>
              <Input.TextArea
                value={planNote}
                onChange={(event) => setPlanNote(event.target.value)}
                autoSize={{ minRows: 3, maxRows: 4 }}
                placeholder='Ulaşım veya plan ile ilgili not giriniz'
                style={{ marginTop: 8 }}
                disabled={isReadOnly || updatingPlan}
              />
            </div>

            <Space style={{ marginTop: 18 }}>
              <Button type='primary' className='action-button' onClick={handleCreatePlanSave} loading={updatingPlan} disabled={isReadOnly || updatingPlan}>
                {isEditMode ? 'Güncelle' : 'Kaydet'}
              </Button>
            </Space>
          </div>
        </td>
      </tr>
    );
  };

  const handlePrevWeek = () => {
    const nextWeekStart = weekStart.add(-7, 'day');
    setWeekStart(nextWeekStart);
    setFocusedMonth(getWeekNavigationMonth(nextWeekStart));
  };

  const handleNextWeek = () => {
    const nextWeekStart = weekStart.add(7, 'day');
    setWeekStart(nextWeekStart);
    setFocusedMonth(getWeekNavigationMonth(nextWeekStart));
  };

  const handleCurrentWeek = () => {
    const now = dayjs();
    setWeekStart(startOfWeekMonday(now));
    setFocusedMonth(now.startOf('month'));
  };

  const handleCurrentMonth = () => {
    const now = dayjs().startOf('month');
    setWeekStart(startOfWeekMonday(now));
    setFocusedMonth(now);
  };

  const handleMonthChange = (value: dayjs.Dayjs | null) => {
    if (!value || !value.isValid()) {
      return;
    }

    const selectedMonth = value.startOf('month');
    setWeekStart(startOfWeekMonday(selectedMonth));
    setFocusedMonth(selectedMonth);
  };

  return (
    <Card className='br-30px' loading={loading || dataLoading} bodyStyle={{ padding: 0 }}>
      <div className='planning-inspector-toolbar'>
        <div className='planning-inspector-toolbar__main'>
          <div className='planning-inspector-toolbar__nav'>
            <Space align='center' size={10} wrap>
              <Button onClick={handlePrevWeek}>Önceki Hafta</Button>
              <DatePicker
                picker='month'
                allowClear={false}
                value={focusedMonth}
                onChange={handleMonthChange}
                format='MMMM YYYY'
                placeholder='Ay / Yıl seçiniz'
                className='planning-inspector-toolbar__month-picker'
              />
              <Button onClick={handleNextWeek}>Sonraki Hafta</Button>
              <Button onClick={handleCurrentWeek} className='action-button'>
                Bu Hafta
              </Button>
            </Space>

         
            <div className='planning-inspector-toolbar__range'>
              <Text strong>{rangeLabel}</Text>
            </div>
          </div>

          <div className='planning-inspector-toolbar__filters'>
            <Space size={6}>
              <Text type='secondary' style={{ fontSize: 12 }}>Tümünü Gör</Text>
              <Switch checked={showAllInspectors} onChange={setShowAllInspectors} />
            </Space>
            <Space size={6}>
              <Text type='secondary' style={{ fontSize: 12 }}>Seçili Olanları Getir</Text>
              <Switch checked={showSelectedInspectorsOnly} onChange={setShowSelectedInspectorsOnly} />
            </Space>
          </div>
        </div>

        <div className='planning-inspector-toolbar__search'>
          <Input
            allowClear
            placeholder='İl-İlçe-Meslek-Uzman Ara'
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            style={{ width: 340, maxWidth: '100%', minWidth: 280 }}
          />
        </div>
      </div>

      <div style={{ overflowX: 'auto', borderTop: '1px solid #f0f0f0' }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr>
              <th style={{ minWidth: 220, textAlign: 'left', padding: '12px 14px', borderBottom: '1px solid #f0f0f0' }}>PKM Uzmanı</th>
              {weekDays.map((day, index) => {
                const isHoliday = holidayDateSet.has(day.format('YYYY-MM-DD'));
                return (
                  <th
                    key={day.format('YYYY-MM-DD')}
                    style={{
                      minWidth: 150,
                      textAlign: 'center',
                      padding: '12px 10px',
                      borderBottom: '1px solid #f0f0f0',
                      background: isHoliday ? '#fff1f0' : '#fff',
                      color: isHoliday ? '#cf1322' : undefined,
                    }}
                  >
                    <div>{dayNames[index]}</div>
                    <div style={{ fontSize: 12, opacity: 0.8 }}>{day.format('DD MMM')}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {filteredInspectors.map((inspector) => {
              const isInspectorFormOpen = activePlanCell?.inspectorId === inspector.id;
              const offeringStats = offeringMatchStatsByInspector.get(inspector.id) || [];
              const totalCount = offeringStats.reduce((acc, stat) => acc + stat.total, 0);
              const matchedCount = offeringStats.reduce((acc, stat) => acc + stat.matched, 0);
              const visibleOfferingStats = offeringStats.filter((stat) => stat.matched > 0);
              const hasMatchMetric = totalCount > 0;
              const canCreateForInspector = !hasMatchMetric || matchedCount > 0;
              return (
                <React.Fragment key={inspector.id}>
                  <tr>
                    <td style={{ padding: '12px 14px', borderBottom: '1px solid #f5f5f5' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                        <div style={{ fontWeight: 600 }}>{inspector.name}</div>
                      </div>
                      {visibleOfferingStats.length > 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          {(() => {
                            const showOfferingLabel = visibleOfferingStats.length > 1;
                            return visibleOfferingStats.map((stat) => {
                              const tagColor = stat.matched === 0 ? 'red' : stat.matched === stat.total ? 'green' : 'orange';
                              const tooltipContent = stat.missingNames.length > 0 ? (
                                <div style={{ maxWidth: 360 }}>
                                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Kontrol Yapamadığı Parametreler</div>
                                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    {stat.missingNames.map((name) => (
                                      <li key={`${stat.offeringKey}-${name}`}>{name}</li>
                                    ))}
                                  </ul>
                                </div>
                              ) : 'Tüm parametreleri kontrol edebiliyor';
                              const tagText = showOfferingLabel
                                ? `${stat.label}: ${stat.matched}/${stat.total}`
                                : `${stat.matched}/${stat.total}`;
                              return (
                                <Tooltip key={`${inspector.id}-${stat.offeringKey}`} title={tooltipContent}>
                                  <Tag color={tagColor} style={{ cursor: 'help' }}>
                                    {tagText}
                                  </Tag>
                                </Tooltip>
                              );
                            });
                          })()}
                        </div>
                      ) : null}
                      <div style={{ marginTop: 4 }}>
                        <Text type='secondary' style={{ fontSize: 12 }}>{inspector.profession}</Text>
                      </div>
                    </td>
                    {weekDays.map((day) => {
                      const dateKey = day.format('YYYY-MM-DD');
                      const cellKey = `${inspector.id}-${dateKey}`;
                      const isPastDate = day.isBefore(dayjs().startOf('day'), 'day');
                      const isSameActiveCell =
                        activePlanCell?.inspectorId === inspector.id && activePlanCell?.dateKey === dateKey;
                      const isHoliday = holidayDateSet.has(dateKey);
                      const visibleHolidayEntries = visibleHolidayMap.get(dateKey) || [];
                      const halfDayHolidayEntries = visibleHolidayEntries.filter((holiday) => Boolean(holiday?.halfDay));
                      const hasHalfDayHoliday = halfDayHolidayEntries.length > 0;
                      const leaveEntry = leaveByInspectorAndDay.get(cellKey);
                      const draftEntries = [...(assignmentsByInspectorAndDay.get(cellKey) || [])].sort(compareByStartTime);
                      const appointmentEntries = [...(appointmentAssignmentsByInspectorAndDay.get(cellKey) || [])].sort(compareByStartTime);
                      const reservationEntries = reservationsByInspectorAndDay.get(cellKey) || [];
                      const assignedDraftOfferingKeys = new Set<string>(
                        draftEntries
                          .map((entry) => (entry?.offeringKey || '').toString().trim())
                          .filter((key) => key.length > 0),
                      );
                      const hasAvailablePlanForCell =
                        availableOfferingKeys.length === 0
                          ? draftEntries.length === 0
                          : availableOfferingKeys.some((offeringKey) => !assignedDraftOfferingKeys.has(offeringKey));
                      const canOpenCreateForCell =
                        !isReadOnly &&
                        canCreateForInspector &&
                        !isPastDate &&
                        !isSameActiveCell &&
                        !isHoliday &&
                        !leaveEntry &&
                        reservationEntries.length === 0 &&
                        hasAvailablePlanForCell;
                      const canAddSecondDraftForCell =
                        canOpenCreateForCell &&
                        draftEntries.length === 0;
                      const currentProposalId = Number(proposalId);
                      const currentEntriesFromOtherProposals =
                        Number.isFinite(currentProposalId) && currentProposalId > 0
                          ? appointmentEntries.filter((entry) => {
                              const entryProposalId = Number(entry?.proposalId);
                              return !Number.isFinite(entryProposalId) || entryProposalId !== currentProposalId;
                            })
                          : appointmentEntries;
                      const displayEntries = [...currentEntriesFromOtherProposals, ...draftEntries];
                      const sortedDisplayEntries = [...displayEntries].sort(compareByStartTime);
                      const cellItems = [
                        ...reservationEntries.map((reservation, index) => ({
                          kind: 'reservation' as const,
                          key: `reservation-${reservation.start.valueOf()}-${index}`,
                          start: reservation.start,
                          reservation,
                        })),
                        ...sortedDisplayEntries.map((entry, index) => ({
                          kind: 'plan' as const,
                          key: `${entry.offeringLabel}-${entry.start.valueOf()}-${index}`,
                          start: entry.start,
                          entry,
                        })),
                      ].sort(compareByStartTime);
                      const hasStackContent =
                        hasHalfDayHoliday || Boolean(leaveEntry) || reservationEntries.length > 0 || sortedDisplayEntries.length > 0;
                      const isEmptyCell = !isHoliday && !hasStackContent;

                      return (
                        <td key={cellKey} style={{ padding: 8, borderBottom: '1px solid #f5f5f5' }}>
                          {isHoliday ? (
                            <div style={{ border: '1px solid #ffccc7', borderRadius: 8, padding: '12px 8px', textAlign: 'center', color: '#cf1322', background: '#fff1f0', fontWeight: 600 }}>
                              Resmi Tatil
                            </div>
                          ) : hasStackContent ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {halfDayHolidayEntries.map((holiday, index) => {
                                const holidayName = holiday?.name?.toString().trim() || 'Resmi Tatil';
                                return (
                                  <div
                                    key={`half-day-holiday-${holiday?.id ?? holidayName}-${dateKey}-${index}`}
                                    style={{
                                      border: '1px solid #ffe58f',
                                      borderRadius: 8,
                                      padding: '8px 10px',
                                      background: '#fffbe6',
                                      color: '#ad6800',
                                    }}
                                  >
                                    <div style={{ fontWeight: 600, marginBottom: 2 }}>{holidayName}</div>
                                    <div style={{ fontSize: 12 }}>Yarım Gün Resmi Tatil</div>
                                  </div>
                                );
                              })}
                              {leaveEntry ? (
                                <div style={{ border: '1px solid #ffccc7', borderRadius: 8, padding: '12px 8px', textAlign: 'center', color: '#cf1322', background: '#fff1f0', fontWeight: 600 }}>
                                  {leaveEntry.label}
                                </div>
                              ) : null}
                              {cellItems.map((item) => {
                                if (item.kind === 'reservation') {
                                  const reservationEntry = item.reservation;
                                  return (
                                    <div
                                      key={item.key}
                                      style={{ border: '1px solid #91caff', borderRadius: 8, padding: '10px 8px', background: '#e6f4ff', color: '#003a8c' }}
                                    >
                                      <div style={{ fontWeight: 600, marginBottom: 2 }}>{reservationEntry.label}</div>
                                      <div style={{ fontSize: 12 }}>
                                        {reservationEntry.start.format('HH:mm')} - {reservationEntry.end ? reservationEntry.end.format('HH:mm') : '--:--'}
                                      </div>
                                    </div>
                                  );
                                }

                                const entry = item.entry;
                                const timeRangeLabel = `${entry.start.format('HH:mm')} - ${entry.end ? entry.end.format('HH:mm') : '--:--'}`;
                                const defaultTransportLabel =
                                  transportLabelByValue.get('PUBLIC_TRANSPORT') || 'Toplu Taşıma';
                                const transportLabel =
                                  entry.transportation
                                    ? transportLabelByValue.get(entry.transportation) || entry.transportation
                                    : defaultTransportLabel;

                                return (
                                  <div
                                    key={item.key}
                                    style={{
                                      border: entry.source === 'current' ? '1px solid #d9d9d9' : '1px solid #b7eb8f',
                                      borderRadius: 8,
                                      padding: '10px 8px',
                                      background: entry.source === 'current' ? '#f5f5f5' : '#f6ffed',
                                      color: entry.source === 'current' ? '#434343' : '#135200',
                                    }}
                                  >
                                    {entry.source === 'current' ? (
                                      <div style={{ fontWeight: 600, marginBottom: 2 }}>
                                        {entry.proposalNumber ?? entry.offeringLabel}
                                      </div>
                                    ) : null}
                                    {entry.source === 'current' && entry.companyName ? (
                                      <div style={{ fontSize: 12, marginBottom: 2 }}>{entry.companyName}</div>
                                    ) : null}
                                    {entry.source === 'current' && (entry.city || entry.district) ? (
                                      <div style={{ fontSize: 12, marginBottom: 2 }}>
                                        {[entry.district, entry.city].filter(Boolean).join(' / ')}
                                      </div>
                                    ) : null}
                                    {entry.source === 'draft' ? (
                                      <div style={{ fontSize: 12, marginBottom: 2 }}>
                                        <span style={{ fontWeight: 600 }}>Saat:</span> {timeRangeLabel}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: 12 }}>
                                        {timeRangeLabel}
                                      </div>
                                    )}
                                    {entry.source === 'draft' ? (
                                      <div style={{ fontSize: 12, marginTop: 2 }}>
                                        <span style={{ fontWeight: 600 }}>Ulaşım Türü:</span> {transportLabel}
                                      </div>
                                    ) : null}
                                    {entry.source === 'draft' && !isReadOnly ? (
                                      <Space size={6} style={{ marginTop: 8 }}>
                                        <Button
                                          size='small'
                                          onClick={() => openEditPlanForm(entry)}
                                          disabled={entry.offeringKey == null || entry.slotIndex == null}
                                        >
                                          Düzenle
                                        </Button>
                                        <Popconfirm
                                          title='Seçilen randevu kalıcı olarak silinsin mi?'
                                          okText='Evet'
                                          cancelText='Vazgeç'
                                          onConfirm={() => handleDeletePagePlan(entry)}
                                          disabled={entry.offeringKey == null || entry.slotIndex == null}
                                        >
                                          <Button
                                            size='small'
                                            danger
                                            disabled={entry.offeringKey == null || entry.slotIndex == null}
                                          >
                                            Sil
                                          </Button>
                                        </Popconfirm>
                                      </Space>
                                    ) : null}
                                  </div>
                                );
                              })}
                              {canAddSecondDraftForCell ? (
                                <button
                                  type='button'
                                  onClick={() => openCreatePlanForm(inspector.id, dateKey)}
                                  style={{
                                    width: '100%',
                                    border: '1px dashed #d9d9d9',
                                    borderRadius: 8,
                                    padding: '8px 8px',
                                    textAlign: 'center',
                                    color: '#bfbfbf',
                                    fontSize: 20,
                                    lineHeight: 1,
                                    background: '#fff',
                                    cursor: 'pointer',
                                  }}
                                >
                                  +
                                </button>
                              ) : null}
                            </div>
                          ) : (
                            <button
                              type='button'
                              onClick={() => openCreatePlanForm(inspector.id, dateKey)}
                              disabled={!canOpenCreateForCell}
                              style={{
                                width: '100%',
                                border: isSameActiveCell ? '1px solid #91caff' : '1px dashed #d9d9d9',
                                borderRadius: 8,
                                padding: '10px 8px',
                                textAlign: 'center',
                                color: '#bfbfbf',
                                fontSize: 22,
                                lineHeight: 1,
                                background: isSameActiveCell ? '#e6f4ff' : '#fff',
                                cursor: canOpenCreateForCell ? 'pointer' : 'not-allowed',
                                opacity: canOpenCreateForCell ? 1 : 0.65,
                              }}
                            >
                              {isEmptyCell && canOpenCreateForCell ? '+' : ''}
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {isInspectorFormOpen ? renderCreatePlanForm() : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card >
  );
};

export default InspectorPlanningCard;
